import {Button, Group, Text} from '@mantine/core';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import ModalFooter from 'ui-library/modals/ModalFooter';
import runModal, {RunModalProps} from 'ui-library/runModal';

import {
  isReviewProcessRemovalRetractionEnabled,
  reviewLifecycleChangeMayRetractActiveReviews,
} from './ReviewLifecyclesUtil';

interface ReviewRetractionContext {
  includesTemplateCollections: boolean;
}

interface RetractReviewsConfirmationModalProps extends RunModalProps<boolean>, ReviewRetractionContext {}

export default function RetractReviewsConfirmationModal({
  close,
  includesTemplateCollections,
}: RetractReviewsConfirmationModalProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'RetractReviewsConfirmationModal', element: 'root'})}>
      <Text {...getTestProps({component: 'RetractReviewsConfirmationModal', element: 'message'})}>
        This review process change may reset affected items from <strong>In Review</strong>
        {includesTemplateCollections ? (
          <>
            {' '}
            or <strong>Accepted</strong>
          </>
        ) : null}{' '}
        to <strong>In Progress</strong>. These items must be re-sent for review. The change may take a moment
        to apply.
      </Text>
      <ModalFooter>
        <Group position="right">
          <Button
            variant="secondary"
            onClick={() => close(false)}
            {...getTestProps({component: 'RetractReviewsConfirmationModal', element: 'cancelButton'})}
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={() => close(true)}
            {...getTestProps({component: 'RetractReviewsConfirmationModal', element: 'confirmButton'})}
          >
            Confirm
          </Button>
        </Group>
      </ModalFooter>
    </div>
  );
}

export function confirmReviewLifecycleRetractionIfNeeded(args: {
  initialReviewLifecycleIds: Array<string> | undefined;
  includesTemplateCollections: boolean;
  selectedReviewLifecycleIds: Array<string>;
  supportsLegacyReview: boolean;
}): Promise<boolean> {
  if (!reviewLifecycleChangeMayRetractActiveReviews(args)) {
    return Promise.resolve(true);
  }
  return showRetractReviewsConfirmationModal(args);
}

/**
 * Shows a retraction warning, used by the bulk review lifecycle update flow in the template
 * collections workspace view.
 */
export function confirmReviewLifecycleRetraction(context: ReviewRetractionContext): Promise<boolean> {
  if (!isReviewProcessRemovalRetractionEnabled(context)) {
    return Promise.resolve(true);
  }
  return showRetractReviewsConfirmationModal(context);
}

function showRetractReviewsConfirmationModal(context: ReviewRetractionContext): Promise<boolean> {
  return runModal(RetractReviewsConfirmationModal, context, {title: 'Update review process'}).then(
    (result) => result === true
  );
}
